import { clone, normalize, sortBy } from "../utils";
import Formula from "../singleton/formula";
import Format from "../singleton/format";
import Entity from "../singleton/entity";
import CollectionSorting from "../singleton/collection-sorting";
import IModelRelatedInfo from "../interface/model-related-info";
import IModelRelatedData from "../interface/model-related-data";
import IModelFeedbackGroup from "../interface/model-feedback-group";
import IModelFeedback from "../interface/model-feedback";
import IFieldSortingPrepared from "../interface/field-sorting-prepared";
import IField from "../interface/field";
import IFeedbackResult from "../interface/feedback-result";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import SortingType from "../enum/sorting-type";
import { TextyTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";

export default class Model {

    public id: number;

    public data: IDocument;

    public filtered: boolean = true;

    private raw: IDocument;

    private base: IDocument;

    private entity: IEntity;

    private formatted: string[] | null = null;

    constructor(entity: IEntity, raw: IDocument, base: IDocument) {
        this.entity = entity;
        this.raw = raw;
        this.base = base;
        this.id = raw.id as number;
        this.data = Object.assign({}, this.base, this.raw);
        this.calcFormulas();
    }

    /* Public Mathods */

    public getFormatted(): string[] {
        if (!this.formatted) {
            this.formatted = this.prepareFormatted();
        }
        return this.formatted;
    }

    public setData(raw: IDocument): void {
        this.raw = raw;
        this.data = Object.assign({}, this.data, this.base, this.raw);
        this.formatted = null;
    }

    public resetEntity(entity: IEntity, base: IDocument): void {
        this.entity = entity;
        this.base = base;
        this.data = Object.assign({}, this.data, this.base, this.raw);
        this.formatted = null;
    }

    public getRaw(): IDocument {
        return clone(this.raw);
    }

    public checkRelatedData(): IModelRelatedInfo[] {
        const result: IModelRelatedInfo[] = [];
        const pointers: IField[] = Entity.filterFieldsByType(this.entity, [FieldType.pointer]);
        pointers.forEach((p: IField) => {
            const ptValue: number | null = (this.data[`c${p.id}`] as number[])[0] || null;
            const model: IDocument | null = (this.data[`d${p.id}`] as IDocument[])[0] || null;
            if (ptValue) {
                if (!model || ptValue !== model.id) {
                    result.push({
                        id: this.id,
                        fieldId: p.id,
                        entityId: p.pointer[1].ptEcn,
                        recordId: ptValue || 0
                    });
                }
            } else {
                this.data[`d${p.id}`] = [];
            }
        });
        return result;
    }

    public setRelatedData(info: IModelRelatedInfo[], data: IModelRelatedData): void {
        info.forEach((i: IModelRelatedInfo) => {
            this.data[`d${i.fieldId}`] = [];
            const doc: IDocument | null = data[i.entityId].find((d: IDocument) => {
                return d.id === i.recordId;
            }) || null;
            if (doc) {
                this.data[`d${i.fieldId}`] = [doc];
            } else {
                this.data[`d${i.fieldId}`] = [];
            }
        });
    }

    public calcFormulas(): void {
        Formula.processDocument(this.entity, this.data);
    }

    public resetFormatted(): void {
        if (this.formatted) {
            this.formatted = this.prepareFormatted();
        }
    }

    public prepareSortingValues(sorting: IFieldSortingPrepared[]): void {
        sorting.forEach((sorting: IFieldSortingPrepared) => {
            this.data[`s${sorting.fieldId}`] = CollectionSorting.getValue(this.data, sorting);
        });
    }

    public addFeedback(item: IFeedbackResult): void {
        const feedback: IDocumentValue = this.getFeedbackProp();
        feedback.items.push(item);
        sortBy(feedback.items, 'webhookId', SortingType.Descending);
        const group: IModelFeedbackGroup | null = feedback.groups.find((g: IModelFeedbackGroup) => {
            return g.status === item.status;
        }) || null;
        if (group) {
            group.items.push(item);
            if (item.webhookId > group.latest) {
                group.latest = item.webhookId;
            }
        } else {
            feedback.groups.push({
                latest: item.webhookId,
                status: item.status,
                items: [item]
            });
        }
        sortBy(feedback.groups, 'latest', SortingType.Descending);
    }

    public clearFeedback(): void {
        delete this.data[`${Global.FEEDBACK_PROP_NAME}`];
    }

    /* Private Mathods */

    private getFeedbackProp(): IModelFeedback {
        const prop: string = `${Global.FEEDBACK_PROP_NAME}`;
        if (!this.data[prop]) {
            this.data[prop] = { documentId: this.data.id as number, groups: [], items: [] };
        }
        return this.data[prop] as IModelFeedback;
    }

    private prepareFormatted(): string[] {
        const result: string[] = [];
        this.entity.fields.forEach((field: IField) => {
            if (TextyTypes.includes(field.uiType)) {
                let prefix: string = 'c';
                if (field.uiType === FieldType.linked || field.uiType === FieldType.formula) {
                    prefix = 'd';
                }
                const value: string = Format.byField(this.data[`${prefix}${field.id}`], field);
                if (value) {
                    result.push(normalize(value.toLowerCase()));
                }
            }
        });
        return result;
    }

}